import {
  sameFace,
  sameSquare,
  type Action,
  type Face,
  type PlayLinkAction,
  type Square,
} from '../engine';

/**
 * Link input (§19.5), derived entirely from legalActions: choose a card, then the pawn (first
 * link), then for a Wild the face and the collateral, then the destination. Yellow moves are built
 * hop by hop with a "stop here" option. Before a pawn is chosen, a card's destinations for every
 * pawn are offered at once: a destination only one pawn can reach chooses that pawn.
 */

export interface LinkSelection {
  readonly cardId: string;
  readonly pawnId: string | null;
  readonly face: Face | null;
  readonly collateral: string | null;
  readonly hops: readonly Square[];
}

export type LinkStageName = 'pawn' | 'face' | 'collateral' | 'target';

export interface LinkStage {
  readonly stage: LinkStageName;
  /** Selection with every forced choice filled in. */
  readonly selection: LinkSelection;
  readonly pawnChoices: readonly string[];
  readonly faceChoices: readonly Face[];
  readonly collateralChoices: readonly string[];
  /** Legal actions consistent with the selection. */
  readonly candidates: readonly PlayLinkAction[];
  /**
   * Destination squares (non-Yellow) or next-hop squares (Yellow). While the pawn is still to be
   * chosen (not for Wilds), the first destinations of every pawn that can play the card.
   */
  readonly squares: readonly Square[];
  /** Yellow: the action that stops after the hops chosen so far, when legal. */
  readonly stopHere: PlayLinkAction | null;
  readonly yellow: boolean;
}

/** A link that moves a pawn (a Trick, which moves nothing, is played from its own dialog). */
export const isLinkAction = (a: Action): a is PlayLinkAction =>
  a.type === 'PLAY_LINK' && !('trick' in a.target);

/** A Trick's link (Long Night). */
export const isTrickAction = (a: Action): a is PlayLinkAction =>
  a.type === 'PLAY_LINK' && 'trick' in a.target;

export const landingsOf = (a: PlayLinkAction): readonly Square[] =>
  'to' in a.target ? [a.target.to] : 'hops' in a.target ? a.target.hops : [];

const uniq = <T>(items: readonly T[], eq: (a: T, b: T) => boolean): T[] => {
  const out: T[] = [];
  for (const it of items) if (!out.some((o) => eq(o, it))) out.push(it);
  return out;
};

/** Computes the current stage of link input for one selected card. */
export function linkStage(legal: readonly Action[], selection: LinkSelection): LinkStage {
  const forCard = legal.filter(isLinkAction).filter((a) => a.cardId === selection.cardId);
  let sel = selection;
  const pawnChoices = uniq(
    forCard.map((a) => a.pawnId ?? ''),
    (a, b) => a === b,
  ).filter(Boolean);
  if (!sel.pawnId && pawnChoices.length === 1) sel = { ...sel, pawnId: pawnChoices[0] as string };
  let candidates = sel.pawnId ? forCard.filter((a) => a.pawnId === sel.pawnId) : forCard;
  const wild = forCard.some((a) => !!a.wild);
  let faceChoices: Face[] = [];
  let collateralChoices: string[] = [];
  if (wild && sel.pawnId) {
    faceChoices = uniq(
      candidates.map((a) => a.wild!.face),
      sameFace,
    );
    if (!sel.face && faceChoices.length === 1) sel = { ...sel, face: faceChoices[0] as Face };
    if (sel.face) {
      candidates = candidates.filter((a) => sameFace(a.wild!.face, sel.face as Face));
      collateralChoices = uniq(
        candidates.map((a) => a.wild!.collateralPawnId),
        (a, b) => a === b,
      );
      if (!sel.collateral && collateralChoices.length === 1)
        sel = { ...sel, collateral: collateralChoices[0] as string };
      if (sel.collateral)
        candidates = candidates.filter((a) => a.wild!.collateralPawnId === sel.collateral);
    }
  }
  const stage: LinkStageName = !sel.pawnId
    ? 'pawn'
    : wild && !sel.face
      ? 'face'
      : wild && !sel.collateral
        ? 'collateral'
        : 'target';
  const yellow = candidates.some((a) => 'hops' in a.target);
  let squares: Square[] = [];
  let stopHere: PlayLinkAction | null = null;
  if (stage === 'pawn' && !wild)
    squares = uniq(
      candidates.map((a) => landingsOf(a)[0] as Square),
      sameSquare,
    );
  if (stage === 'target') {
    if (yellow) {
      const prefix = sel.hops;
      const matching = candidates.filter((a) => {
        const hops = landingsOf(a);
        return hops.length >= prefix.length && prefix.every((h, i) => sameSquare(h, hops[i]));
      });
      candidates = matching;
      squares = uniq(
        matching
          .filter((a) => landingsOf(a).length > prefix.length)
          .map((a) => landingsOf(a)[prefix.length] as Square),
        sameSquare,
      );
      stopHere =
        prefix.length > 0
          ? (matching.find((a) => landingsOf(a).length === prefix.length) ?? null)
          : null;
    } else {
      squares = uniq(
        candidates.map((a) => landingsOf(a)[0] as Square),
        sameSquare,
      );
    }
  }
  return {
    stage,
    selection: sel,
    pawnChoices,
    faceChoices,
    collateralChoices,
    candidates,
    squares,
    stopHere,
    yellow,
  };
}

/** The action a destination click completes (non-Yellow), or null. */
export function actionForSquare(stage: LinkStage, sq: Square): PlayLinkAction | null {
  if (stage.stage !== 'target' || stage.yellow) return null;
  return stage.candidates.find((a) => sameSquare(landingsOf(a)[0], sq)) ?? null;
}

/** Yellow: the action of stopping right after hopping to `sq`, if that is legal. */
export function hopAction(stage: LinkStage, sq: Square): PlayLinkAction | null {
  if (stage.stage !== 'target' || !stage.yellow) return null;
  const hops = [...stage.selection.hops, sq];
  return (
    stage.candidates.find((a) => {
      const l = landingsOf(a);
      return l.length === hops.length && hops.every((h, i) => sameSquare(h, l[i]));
    }) ?? null
  );
}

/** Yellow: whether a hop to `sq` can be followed by more hops. */
export const hopContinues = (stage: LinkStage, sq: Square): boolean =>
  stage.candidates.some((a) => {
    const l = landingsOf(a);
    const hops = [...stage.selection.hops, sq];
    return l.length > hops.length && hops.every((h, i) => sameSquare(h, l[i]));
  });

/** Before a pawn is chosen: the pawns whose move with this card can start by landing on `sq`. */
export function pawnsForSquare(stage: LinkStage, sq: Square): string[] {
  if (stage.stage !== 'pawn') return [];
  return uniq(
    stage.candidates
      .filter((a) => sameSquare(landingsOf(a)[0], sq))
      .map((a) => a.pawnId ?? '')
      .filter(Boolean),
    (a, b) => a === b,
  );
}

/*
 * Drag and drop: a pawn or a card dropped on a square aims the move at that square. The same
 * legal actions decide what the drop means; whatever it leaves open is asked as a tap would be.
 */

/** The last square a link lands on. */
export const finalLanding = (a: PlayLinkAction): Square => {
  const l = landingsOf(a);
  return l[l.length - 1] as Square;
};

const samePath = (a: readonly Square[], b: readonly Square[]): boolean =>
  a.length === b.length && a.every((s, i) => sameSquare(s, b[i]));

/** Whether a legal link agrees with every choice made so far. */
function fits(a: PlayLinkAction, sel: LinkSelection): boolean {
  if (a.cardId !== sel.cardId) return false;
  if (sel.pawnId && a.pawnId !== sel.pawnId) return false;
  if (sel.face && !(a.wild && sameFace(a.wild.face, sel.face))) return false;
  if (sel.collateral && a.wild?.collateralPawnId !== sel.collateral) return false;
  const l = landingsOf(a);
  return sel.hops.length <= l.length && sel.hops.every((h, i) => sameSquare(h, l[i]));
}

/** What a drop means: the one move it makes, or the selection to go on from and what to ask. */
export type Aim =
  | { readonly action: PlayLinkAction }
  | {
      readonly selection: LinkSelection;
      readonly ask: 'pawn' | 'face' | 'collateral' | 'hops';
      /** The pawns asked about ('pawn'). */
      readonly pawns?: readonly string[];
    };

/**
 * The move aimed at `goal`: the legal links that agree with the selection and end on `goal`.
 * One link is the move. Several are narrowed as far as they agree (pawn, then Wild face, then
 * collateral, then Yellow hops) and the first difference is asked. A Yellow move may pass
 * through `goal` without being able to stop there: the hops up to it are taken, the rest asked.
 * Null when no legal link reaches `goal`.
 */
export function aimLink(
  legal: readonly Action[],
  selection: LinkSelection,
  goal: Square,
): Aim | null {
  const fitting = legal.filter(isLinkAction).filter((a) => fits(a, selection));
  const ending = fitting.filter((a) => sameSquare(finalLanding(a), goal));
  if (ending.length === 1) return { action: ending[0] as PlayLinkAction };
  // The links to consider, each cut at `goal`: those that end there, else those that pass it.
  const reaching =
    ending.length > 0
      ? ending
      : fitting.filter((a) =>
          landingsOf(a).some((s, i) => i >= selection.hops.length && sameSquare(s, goal)),
        );
  if (reaching.length === 0) return null;
  let sel = selection;
  const pawns = uniq(
    reaching.map((a) => a.pawnId ?? ''),
    (a, b) => a === b,
  ).filter(Boolean);
  if (pawns.length > 1) return { selection: sel, ask: 'pawn', pawns };
  if (pawns.length === 1) sel = { ...sel, pawnId: pawns[0] as string };
  const faces = uniq(
    reaching.flatMap((a) => (a.wild ? [a.wild.face] : [])),
    sameFace,
  );
  if (faces.length > 1) return { selection: sel, ask: 'face' };
  if (faces.length === 1) sel = { ...sel, face: faces[0] as Face };
  const collaterals = uniq(
    reaching.flatMap((a) => (a.wild ? [a.wild.collateralPawnId] : [])),
    (a, b) => a === b,
  );
  if (collaterals.length > 1) return { selection: sel, ask: 'collateral' };
  if (collaterals.length === 1) sel = { ...sel, collateral: collaterals[0] as string };
  // Yellow: the hops every way to `goal` shares.
  const paths = uniq(
    reaching.map((a) => {
      const l = landingsOf(a);
      return l.slice(0, l.findIndex((s, i) => i >= sel.hops.length && sameSquare(s, goal)) + 1);
    }),
    samePath,
  );
  let common = paths[0] ?? [];
  for (const p of paths) {
    let n = 0;
    while (n < common.length && n < p.length && sameSquare(common[n], p[n])) n++;
    common = common.slice(0, n);
  }
  if (common.length > sel.hops.length) sel = { ...sel, hops: common };
  return { selection: sel, ask: 'hops' };
}

/** Cards in hand that can move `pawnId` so that it ends on `goal`, one per card id. */
export function cardsForMove(legal: readonly Action[], pawnId: string, goal: Square): string[] {
  return uniq(
    legal
      .filter(isLinkAction)
      .filter((a) => a.pawnId === pawnId && sameSquare(finalLanding(a), goal))
      .map((a) => a.cardId),
    (a, b) => a === b,
  );
}

/** Squares `pawnId` can end on with any card in hand: where a dragged pawn may be dropped. */
export function dropSquares(legal: readonly Action[], pawnId: string): Square[] {
  return uniq(
    legal
      .filter(isLinkAction)
      .filter((a) => a.pawnId === pawnId)
      .map(finalLanding),
    sameSquare,
  );
}
